/**
 * PhaseBreadcrumb — shows a case's progress across Discovery → Analysis → Design
 * with a single-click CTA to continue the next pending phase.
 *
 * Mounted at the top of CaseDetail so the user always knows where the case
 * sits in the pipeline AND can jump directly into the relevant wizard
 * without going back to the Case Library.
 *
 * Visual:
 *   [✓ Discovery]  →  [● Analysis]  →  [○ Design]    [Continue to Analysis →]
 *
 * Status mapping:
 *   - "Completed"   → green check, green text
 *   - "In Progress" → amber dot, amber text (this is the *current* phase)
 *   - "Not Started" → muted circle, muted text
 *
 * Returns null when no caseId/currentPhase available so callers can mount
 * unconditionally above the section switcher.
 */
import { CheckCircle2, Circle, ChevronRight, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';

type Phase = 'Discovery' | 'Analysis' | 'Design' | 'Completed';
type PhaseStatus = 'Not Started' | 'In Progress' | 'Completed';

interface PhaseBreadcrumbProps {
  caseId: string | null | undefined;
  currentPhase?: Phase | null;
  discoveryPhaseStatus?: PhaseStatus | null;
  analysisPhaseStatus?: PhaseStatus | null;
  designPhaseStatus?: PhaseStatus | null;
}

interface PhaseConfig {
  key: 'Discovery' | 'Analysis' | 'Design';
  label: string;
  /** Route to navigate when the user clicks "Continue to {phase}". */
  route: (caseId: string) => string;
}

const PHASES: PhaseConfig[] = [
  {
    key: 'Discovery',
    label: 'Discovery',
    route: (id) => `/cases/new-wizard?caseId=${id}`,
  },
  {
    key: 'Analysis',
    label: 'Analysis',
    route: (id) => `/cases/analysis-wizard?caseId=${id}`,
  },
  {
    key: 'Design',
    label: 'Design',
    route: (id) => `/cases/design-wizard?caseId=${id}`,
  },
];

/** Status palette for the chip — green = done, amber = active, slate = not started. */
function chipClasses(status: PhaseStatus): string {
  switch (status) {
    case 'Completed':
      return 'text-green-700 bg-green-50 border-green-200';
    case 'In Progress':
      return 'text-amber-700 bg-amber-50 border-amber-300 ring-2 ring-amber-100';
    case 'Not Started':
    default:
      return 'text-neutral-500 bg-neutral-50 border-neutral-200';
  }
}

function StatusIcon({ status }: { status: PhaseStatus }) {
  if (status === 'Completed') {
    return <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />;
  }
  if (status === 'In Progress') {
    return <Circle className="w-4 h-4 text-amber-500 flex-shrink-0 fill-amber-500" />;
  }
  return <Circle className="w-4 h-4 text-neutral-300 flex-shrink-0" />;
}

export default function PhaseBreadcrumb({
  caseId,
  currentPhase,
  discoveryPhaseStatus,
  analysisPhaseStatus,
  designPhaseStatus,
}: PhaseBreadcrumbProps) {
  const navigate = useNavigate();

  if (!caseId || !currentPhase) return null;

  const statusByPhase: Record<'Discovery' | 'Analysis' | 'Design', PhaseStatus> = {
    Discovery: discoveryPhaseStatus ?? 'Not Started',
    Analysis: analysisPhaseStatus ?? 'Not Started',
    Design: designPhaseStatus ?? 'Not Started',
  };

  // CTA target: the first phase that is *not* Completed.  If currentPhase is
  // already Discovery / Analysis / Design we route to that one; if everything
  // is done, the case is fully Completed and the CTA is hidden.
  const nextPhase: PhaseConfig | null = (() => {
    if (currentPhase === 'Completed') return null;
    const match = PHASES.find((p) => statusByPhase[p.key] !== 'Completed');
    return match ?? null;
  })();

  return (
    <div className="flex items-center justify-between gap-4 px-4 py-2.5 bg-white border border-neutral-200 rounded-lg mb-4">
      {/* Phase chip rail */}
      <div className="flex items-center gap-2 min-w-0 flex-wrap">
        {PHASES.map((phase, idx) => {
          const status = statusByPhase[phase.key];
          return (
            <div key={phase.key} className="flex items-center gap-2">
              <div
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${chipClasses(
                  status,
                )}`}
                title={`${phase.label} · ${status}`}
              >
                <StatusIcon status={status} />
                <span>{phase.label}</span>
                {status === 'In Progress' && (
                  <span className="text-[10px] uppercase tracking-wide opacity-70">current</span>
                )}
              </div>
              {idx < PHASES.length - 1 && (
                <ChevronRight className="w-4 h-4 text-neutral-300 flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>

      {/* Continue CTA — single button that jumps to the next pending phase's wizard */}
      {nextPhase && (
        <Button
          variant="primary"
          size="sm"
          onClick={() => navigate(nextPhase.route(caseId))}
          className="flex items-center gap-2 flex-shrink-0"
        >
          Continue to {nextPhase.label}
          <ArrowRight className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}
